import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { brand, values, testimonials } from '@/mocks/site';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dafbq6ptto0ceqqnh9pg';

const aboutFaqs = [
  {
    q: '投稿会被公开吗？署名如何处理？',
    a: '每条投稿都会由编辑审核。如需公开，我们会先与你确认并按你的要求选择匿名或署名，绝不在未确认的情况下暴露隐私。',
  },
  {
    q: '投稿或爆料需要付费吗？',
    a: '不需要。投稿爆料全程免费，我们更看重素材的真实性与价值，而非任何形式的费用。',
  },
  {
    q: '一般多久能得到回复？',
    a: '编辑会在 3 个工作日内处理投稿。若素材进入跟进报道，我们会主动通过你留下的联系方式与渠道沟通进度。',
  },
  {
    q: '我提供的内容会如何被使用？',
    a: '我们会对素材进行核实、整理与脱敏后形成客观报道，并注明线索来源。涉及版权的内容需你先确认拥有相应权利。',
  },
];

type FormStatus = 'idle' | 'success' | 'error';

export default function About() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [formError, setFormError] = useState('');
  const [faqOpen, setFaqOpen] = useState(0);

  useSeo({
    title: '关于我们 / 投稿爆料 - 热瓜速递',
    description:
      '了解热瓜速递的品牌定位、内容原则与发展理念。想爆料、投稿或提供线索？通过投稿联系表单告诉我们，还有用户口碑与常见问题一览。',
    keywords: '关于热瓜速递,投稿爆料,热瓜速递内容原则',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于热瓜速递',
        description: '热瓜速递的品牌介绍、定位、内容原则与联系方式。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: aboutFaqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // honeypot 反垃圾：命中则静默通过，不真正提交
    const honeypot = (fd.get('company_alt') as string | null)?.trim() ?? '';
    if (honeypot) {
      setStatus('success');
      return;
    }
    fd.delete('company_alt');

    const msg = (fd.get('message') as string) ?? '';
    if (!msg.trim()) {
      setStatus('error');
      setFormError('请填写详细内容后再提交。');
      return;
    }

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam|form data is spam/i.test(responseText);

      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        form.reset();
        setStatus('success');
        setFormError('');
      } else {
        setStatus('error');
        setFormError(serverMsg ? serverMsg : '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络似乎开小差了，请稍后再试。');
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6">
      <nav className="flex items-center gap-1.5 py-4 text-xs text-foreground-400" aria-label="面包屑">
        <Link to="/" className="inline-flex items-center gap-1 transition-colors hover:text-primary-600">
          <i className="ri-home-5-line" aria-hidden="true" /> 首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
        <span className="text-foreground-600">关于我们</span>
      </nav>

      {/* 头图 */}
      <section className="relative overflow-hidden rounded-lg">
        <div className="relative aspect-[16/6] w-full overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=watermelon%20slices%20and%20sparkling%20light%20particles%20arranged%20on%20a%20soft%20warm%20cream%20background%2C%20cheerful%20editorial%20collage%20for%20a%20lifestyle%20media%20brand%2C%20playful%20and%20modern%2C%20high%20key%20lighting&width=1600&height=600&seq=about-hero-01&orientation=landscape"
            alt="关于热瓜速递品牌插画"
            title="关于热瓜速递"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 via-foreground-950/30 to-transparent" />
        </div>
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-10">
          <span className="inline-flex w-max items-center gap-1 rounded-full bg-accent-400/90 px-2.5 py-1 text-xs font-bold text-primary-950">
            <i className="ri-information-line" aria-hidden="true" /> ABOUT
          </span>
          <h1 className="mt-3 text-2xl font-black text-background-50 md:text-4xl">关于热瓜速递</h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base">
            一个认真做内容、轻松吃好瓜的中文资讯门户。{brand.slogan}。
          </p>
        </div>
      </section>

      {/* 定位 */}
      <section className="mt-8 grid gap-6 lg:grid-cols-2" aria-label="品牌介绍">
        <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
            <i className="ri-flag-2-line text-primary-600" aria-hidden="true" /> 我们是谁
          </h2>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            热瓜速递诞生于一个朴素的愿望——在娱乐八卦、社会热点、明星资讯、网红爆料与影视综艺的信息洪流里，
            为忙碌的普通人守住一片既能轻松吃瓜、又能保持清醒的园地。我们把多元栏目按兴趣分类，让你想看什么就点什么，
            不必在海量信息里大海捞针。
          </p>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            我们相信，好的“瓜”不只是茶余饭后的谈资，更是理解流行文化、洞察时代情绪的一扇小窗。
            热闹要有，理性也要有，这正是热瓜速递想传递的态度。
          </p>
        </div>
        <div className="rounded-lg border border-background-200/70 bg-background-50 p-6">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
            <i className="ri-compass-line text-primary-600" aria-hidden="true" /> 网站定位
          </h2>
          <ul className="mt-3 space-y-3 text-sm leading-7 text-foreground-600">
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line mt-1 text-accent-600" aria-hidden="true" />
              <span><strong className="text-foreground-800">面向吃瓜爱好者：</strong>五大门类内容聚合，信息组织清晰、分类直达。</span>
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line mt-1 text-accent-600" aria-hidden="true" />
              <span><strong className="text-foreground-800">面向内容需求方：</strong>轻量、易读、有观点，兼顾资讯性与陪伴感。</span>
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line mt-1 text-accent-600" aria-hidden="true" />
              <span><strong className="text-foreground-800">面向话题创作者：</strong>开放投稿爆料，让真实、优质的内容有处安放。</span>
            </li>
          </ul>
        </div>
      </section>

      {/* 内容原则 */}
      <section className="mt-8" aria-labelledby="values-title">
        <div className="mb-4 border-b border-background-200/70 pb-3">
          <h2 id="values-title" className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
            <i className="ri-heart-3-line text-primary-600" aria-hidden="true" /> 我们坚持的内容原则
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v) => (
            <div key={v.title} className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                <i className={v.icon} aria-hidden="true" />
              </span>
              <h3 className="mt-3 text-base font-bold text-foreground-900">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 用户口碑 */}
      <section className="mt-8" aria-labelledby="say-title">
        <div className="mb-4 border-b border-background-200/70 pb-3">
          <h2 id="say-title" className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
            <i className="ri-chat-smile-2-line text-primary-600" aria-hidden="true" /> 用户怎么说
          </h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.name} className="flex flex-col rounded-lg border border-background-200/70 bg-background-50 p-5">
              <div className="flex gap-0.5 text-accent-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm" aria-hidden="true" />
                ))}
              </div>
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">
                “{t.text}”
              </blockquote>
              <figcaption className="mt-4 flex items-center gap-2">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 text-sm font-bold text-secondary-800">
                  {t.name.slice(0, 1)}
                </span>
                <div className="leading-tight">
                  <p className="text-sm font-semibold text-foreground-800">{t.name}</p>
                  <p className="text-xs text-foreground-400">{t.role}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {/* FAQ */}
        <section id="faq" aria-labelledby="faq-title" className="scroll-mt-24">
          <div className="mb-4 border-b border-background-200/70 pb-3">
            <h2 id="faq-title" className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
              <i className="ri-question-answer-line text-primary-600" aria-hidden="true" /> 投稿与常见问题
            </h2>
          </div>
          <div className="divide-y divide-background-200/70 rounded-lg border border-background-200/70 bg-background-50">
            {aboutFaqs.map((f, i) => {
              const isOpen = faqOpen === i;
              return (
                <div key={f.q}>
                  <button
                    type="button"
                    onClick={() => setFaqOpen(isOpen ? -1 : i)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
                    aria-expanded={isOpen}
                  >
                    <span className="text-sm font-semibold text-foreground-900">{f.q}</span>
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                      <i className={`ri-add-line transition-transform ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" />
                    </span>
                  </button>
                  {isOpen && (
                    <p className="px-4 pb-4 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* 联系 / 投稿表单 */}
        <section id="contact" aria-labelledby="contact-title" className="scroll-mt-24">
          <div className="mb-4 border-b border-background-200/70 pb-3">
            <h2 id="contact-title" className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
              <i className="ri-edit-2-line text-primary-600" aria-hidden="true" /> 投稿爆料 / 联系我们
            </h2>
          </div>
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
            <p className="text-sm leading-relaxed text-foreground-500">
              有线索、有槽点、有追更心得？填好下方表单，编辑团队会认真审核每一条投稿。
              带 <span className="text-primary-600">*</span> 为必填项。
            </p>

            <form data-readdy-form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-foreground-700">你的称呼</span>
                  <input
                    type="text"
                    name="name"
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-300"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-foreground-700">联系邮箱</span>
                  <input
                    type="email"
                    name="email"
                    placeholder="便于我们回复进度"
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-300"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground-700">
                  内容类型 <span className="text-primary-600">*</span>
                </span>
                <select
                  name="topic"
                  defaultValue="娱乐八卦线索"
                  className="w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-300"
                >
                  {['娱乐八卦线索', '网红 / 直播内幕', '影视综艺讨论', '社会热点事件', '纠错反馈', '合作联系'].map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground-700">主题标题</span>
                <input
                  type="text"
                  name="subject"
                  placeholder="用一句话概括你想说的内容"
                  className="w-full rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-300"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground-700">
                  详细内容 <span className="text-primary-600">*</span>
                </span>
                <textarea
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  placeholder="请尽量描述清楚时间、人物与经过，线索越具体越有助于核实…"
                  className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-300"
                />
                <span className="mt-1 block text-right text-xs text-foreground-400">最多 500 字</span>
              </label>

              {/* anti-spam honeypot（对用户隐藏） */}
              <input
                type="text"
                name="company_alt"
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                readOnly
                className="aux-field"
              />

              {status === 'success' && (
                <p className="rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-line mr-1" aria-hidden="true" />
                  感谢你的投稿！我们已收到内容，编辑会尽快处理并与你联系。
                </p>
              )}
              {status === 'error' && (
                <p className="rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line mr-1" aria-hidden="true" />
                  {formError}
                </p>
              )}

              <button
                type="submit"
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-send-plane-line" aria-hidden="true" /> 提交投稿
              </button>

              <div className="flex items-center gap-2 text-xs text-foreground-400">
                <i className="ri-checkbox-circle-line rounded-full px-2 py-0.5" aria-hidden="true" />
                也欢迎邮件联系：
                <a href={`mailto:${brand.email}`} className="font-medium text-primary-600 underline underline-offset-2">
                  {brand.email}
                </a>
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}